<script setup lang="ts">
/**
 * QrCustomizerModal — 1:1 порт docs/mockups/modal-qr-customizer.html (inbox-180).
 *
 * Split 2-col: controls (left) + live preview (right). Wire QrApi.create/patch.
 *
 * Open: modals.open('qr-customizer', { linkId, qrId?: number }).
 */

import { computed, nextTick, onMounted, ref, watch } from 'vue';
import QRCode from 'qrcode';
import { useModalStore } from '@/stores/modal';
import { useMeStore } from '@/stores/me';
import { LinksApi, ApiError, type Link } from '@/api/links';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiField from '@/components/ui/UiField.vue';
import UiSelect from '@/components/ui/UiSelect.vue';
import UiDevCard from '@/components/ui/UiDevCard.vue';

const modals = useModalStore();
const me = useMeStore();

// inbox-258 — plan-gating. Free тариф: только PNG, без логотипа,
// фиксированная density M. Pro+: все форматы + лого + кастом density.
type EcLevel = 'L' | 'M' | 'Q' | 'H';
type QrFormat = 'PNG' | 'SVG' | 'EPS' | 'PDF';
const PRO_PLANS = new Set(['starter', 'business', 'team', 'enterprise']);
const isPro = computed(() => {
  const plan = me.me?.organization_plan;
  return !!plan && PRO_PLANS.has(plan);
});
const formatLocked = (f: QrFormat): boolean => !isPro.value && f !== 'PNG';
const logoLocked = computed(() => !isPro.value);
const densityLocked = computed(() => !isPro.value);
const EC_LEVELS: EcLevel[] = ['L', 'M', 'Q', 'H'];
const EC_HINTS: Record<EcLevel, string> = {
  L: 'Низкая · ~7% коррекции',
  M: 'Средняя · ~15%',
  Q: 'Выше · ~25%',
  H: 'Высокая · ~30% (для лого)',
};

const targetUrl = ref('https://link.helmix.app/short');
const colorFg = ref('#0a0d12');
const colorBg = ref('#ffffff');
const shape = ref<'squares' | 'rounded' | 'dots'>('squares');
const frame = ref<'none' | 'simple' | 'with-text' | 'scannable'>('none');
const format = ref<QrFormat>('PNG');
const logoUrl = ref<string | null>(null);
const logoName = ref<string | null>(null);
const submitting = ref(false);
const error = ref<string | null>(null);

// inbox-258 — density (error-correction level). Free: фиксированно 'M';
// Pro+: пользователь выбирает L/M/Q/H. H рекомендуется когда есть лого
// (выше resilience к перекрытию центра).
const ecLevel = ref<EcLevel>('M');

// inbox-276 — required link dropdown. Без выбранной ссылки backend
// создавал бы orphan QR (link_id NULL); фронт теперь блокирует submit.
const links = ref<Link[]>([]);
const linksLoading = ref(false);
const selectedLinkId = ref<string>(''); // '' → не выбрано (UiSelect v-model string)

const linkOptions = computed(() =>
  links.value.map((l) => ({
    value: String(l.id),
    label: `${l.short_url} — ${l.title || l.target_url}`,
  })),
);

async function loadLinks(): Promise<void> {
  linksLoading.value = true;
  try {
    links.value = await LinksApi.list({ status: 'active', limit: 200 });
    // Pre-fill: payload.linkId или URL автозаполняется из выбранной.
    const payload = modals.payload as { linkId?: number } | null;
    if (payload?.linkId && links.value.some((l) => l.id === payload.linkId)) {
      selectedLinkId.value = String(payload.linkId);
    }
  } catch {
    // 404 (нет endpoint'а) / 401 — оставляем пустой массив; UiSelect
    // покажет hint про создание ссылки сначала.
    links.value = [];
  } finally {
    linksLoading.value = false;
  }
}

onMounted(() => {
  if (modals.activeKey === 'qr-customizer') {
    void loadLinks();
    // inbox-233 — initial client-side render после mount (canvas ref готов).
    void nextTick(() => renderClientPreview());
  }
});

watch(() => modals.activeKey, (k) => {
  if (k !== 'qr-customizer') return;
  const payload = modals.payload as Record<string, unknown> | null;
  targetUrl.value = (payload?.url as string) || 'https://link.helmix.app/short';
  colorFg.value = '#0a0d12';
  colorBg.value = '#ffffff';
  shape.value = 'squares';
  frame.value = 'none';
  format.value = 'PNG';
  logoUrl.value = null;
  logoName.value = null;
  ecLevel.value = 'M';
  selectedLinkId.value = '';
  error.value = null;
  submitting.value = false;
  void loadLinks();
});

// inbox-258 — logo upload через FileReader → data URL. Без backend
// endpoint'а (POST /api/v1/qr-codes/{id}/logo не задеплоен — see
// commit 660d2ed roadmap), data URL уходит в emit-payload как logo_url
// → backend сохранит при появлении endpoint'а. 256 KB cap чтобы не
// раздувать customization JSON.
const LOGO_MAX_BYTES = 256 * 1024;
function onLogoChange(e: Event): void {
  if (logoLocked.value) return;
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  if (!/^image\/(png|jpe?g|svg\+xml|webp)$/i.test(file.type)) {
    error.value = 'Логотип должен быть PNG / JPEG / SVG / WebP.';
    input.value = '';
    return;
  }
  if (file.size > LOGO_MAX_BYTES) {
    error.value = `Логотип больше ${(LOGO_MAX_BYTES / 1024).toFixed(0)} KB. Ужмите перед загрузкой.`;
    input.value = '';
    return;
  }
  error.value = null;
  const reader = new FileReader();
  reader.onload = () => {
    logoUrl.value = String(reader.result);
    logoName.value = file.name;
  };
  reader.onerror = () => {
    error.value = 'Не удалось прочитать файл.';
  };
  reader.readAsDataURL(file);
}
function removeLogo(): void {
  logoUrl.value = null;
  logoName.value = null;
}

// inbox-276 — синхронизация targetUrl ↔ selected link (превью обновляется).
watch(selectedLinkId, (newId) => {
  if (!newId) return;
  const l = links.value.find((link) => String(link.id) === newId);
  if (l) targetUrl.value = l.short_url;
});

// inbox-233 — client-side preview через qrcode library (~30KB).
// Раньше каждое изменение поля шло на сервер /api/v1/qr-codes/render —
// flicker + load. Теперь preview рисуется на canvas мгновенно.
// Backend render остаётся для download (high-res + logo composition).
const previewCanvas = ref<HTMLCanvasElement | null>(null);
const previewError = ref<string | null>(null);

async function renderClientPreview(): Promise<void> {
  const canvas = previewCanvas.value;
  if (!canvas) return;
  const url = targetUrl.value || ' ';
  try {
    await QRCode.toCanvas(canvas, url, {
      width: 320,
      margin: 1,
      errorCorrectionLevel: ecLevel.value,
      color: {
        dark: colorFg.value,
        light: colorBg.value === 'transparent' ? '#00000000' : colorBg.value,
      },
    });
    previewError.value = null;
  } catch (e) {
    previewError.value = (e as Error).message;
  }
}

watch([targetUrl, colorFg, colorBg, ecLevel], () => {
  void renderClientPreview();
});

// Server-side URL для download — больший size + native format + logo
// (логотип-композиция требует backend Pillow для PNG/SVG/EPS/PDF).
const downloadUrl = computed(() => {
  const u = new URL('/api/v1/qr-codes/render', window.location.origin);
  u.searchParams.set('url', targetUrl.value);
  u.searchParams.set('size', '1024');
  u.searchParams.set('fg', colorFg.value);
  u.searchParams.set('bg', colorBg.value);
  u.searchParams.set('shape', shape.value);
  if (frame.value !== 'none') u.searchParams.set('frame', frame.value);
  u.searchParams.set('format', format.value.toLowerCase());
  return u.toString();
});

const canSubmit = computed(
  () => !!selectedLinkId.value && !submitting.value,
);

async function saveAsTemplate() {
  // inbox-276 — frontend gate: без link_id submit заблокирован.
  if (!selectedLinkId.value) {
    error.value = 'Выберите ссылку, к которой привяжем QR-код.';
    return;
  }
  error.value = null;
  submitting.value = true;
  try {
    // Backend POST /api/v1/qr-codes должен принять link_id + customization;
    // если бэкенд вернёт 422 (link_id missing/invalid) — сохраняем ошибку
    // вместо silent close. Реальный QrApi.create wire — отдельная задача
    // (inbox-257 store + 258 customizer flow). Пока оставляем emit-only,
    // но с гарантированно валидным link_id в payload.
    await new Promise((r) => setTimeout(r, 400));
    // inbox-258 — sanitize plan-locked поля: free не может прислать
    // ничего кроме PNG/без-лого/density=M даже если как-то поправил через
    // devtools. UI-disabled — это soft-gate, это hard-gate перед emit.
    const safeFormat: QrFormat = isPro.value ? format.value : 'PNG';
    const safeLogo = isPro.value ? logoUrl.value : null;
    const safeEc: EcLevel = isPro.value ? ecLevel.value : 'M';
    modals.emit('qr-customizer', {
      link_id: Number(selectedLinkId.value),
      fg: colorFg.value,
      bg: colorBg.value,
      shape: shape.value,
      frame: frame.value,
      format: safeFormat,
      ec_level: safeEc,
      logo_url: safeLogo,
    });
    modals.close();
  } catch (e) {
    if (e instanceof ApiError && e.status === 422) {
      error.value = 'Сервер отверг данные. Выберите другую ссылку или проверьте права.';
    } else {
      error.value = (e as Error).message || 'Не удалось сохранить.';
    }
  } finally {
    submitting.value = false;
  }
}

function close() {
  if (!modals.pending) modals.close();
}
</script>

<template>
  <UiModal
    title="Кастомайзер QR-кода"
    subtitle="Цвета, форма, лого, рамка · превью обновляется в реальном времени"
    size="xl"
    :disable-close="submitting"
    @close="close"
  >
    <div class="qr-split">
      <div class="qr-controls">
        <UiField
          label="Связанная ссылка"
          :hint="linksLoading
            ? 'Загружаем список…'
            : links.length === 0
              ? 'Сначала создайте короткую ссылку в /links — QR без link не имеет смысла.'
              : 'Обязательно. QR будет вести на короткий URL этой ссылки.'"
          :error="!selectedLinkId && error ? 'Выберите ссылку' : ''"
          required
        >
          <UiSelect
            v-model="selectedLinkId"
            :options="linkOptions"
            :disabled="submitting || linksLoading"
            placeholder="Выберите ссылку…"
            name="link_id"
            required
          />
        </UiField>
        <UiField label="Цвет точек (foreground)">
          <input v-model="colorFg" type="color" class="color-picker" />
          <code class="color-hex">{{ colorFg }}</code>
        </UiField>
        <UiField label="Цвет фона (background)">
          <input v-model="colorBg" type="color" class="color-picker" />
          <code class="color-hex">{{ colorBg }}</code>
        </UiField>
        <UiField label="Форма точек">
          <div class="seg-group">
            <label v-for="s in ['squares', 'rounded', 'dots'] as const" :key="s" :class="['seg', { active: shape === s }]">
              <input type="radio" v-model="shape" :value="s" />{{ ({ squares: 'Квадраты', rounded: 'Скругл.', dots: 'Точки' } as Record<string, string>)[s] }}
            </label>
          </div>
        </UiField>
        <UiField label="Рамка">
          <select v-model="frame" class="select">
            <option value="none">Без рамки</option>
            <option value="simple">Простая</option>
            <option value="with-text">С подписью</option>
            <option value="scannable">Сканируйте меня</option>
          </select>
        </UiField>
        <UiField
          label="Формат скачивания"
          :hint="formatLocked('SVG') ? 'Free тариф — только PNG. SVG / EPS / PDF — в Pro и выше.' : 'Векторные форматы для печати, PNG для web.'"
        >
          <div class="seg-group">
            <label
              v-for="f in ['PNG', 'SVG', 'EPS', 'PDF'] as const"
              :key="f"
              :class="['seg', { active: format === f, locked: formatLocked(f) }]"
              :title="formatLocked(f) ? 'Доступно с Pro и выше' : ''"
            >
              <input
                type="radio"
                v-model="format"
                :value="f"
                :disabled="formatLocked(f)"
              />{{ f }}{{ formatLocked(f) ? ' 🔒' : '' }}
            </label>
          </div>
        </UiField>

        <!-- inbox-258: density / error-correction level. Только Pro+. -->
        <UiField
          label="Плотность (коррекция ошибок)"
          :hint="densityLocked ? 'Free — фиксировано M. Кастом — Pro и выше.' : EC_HINTS[ecLevel]"
        >
          <div class="seg-group">
            <label
              v-for="lvl in EC_LEVELS"
              :key="lvl"
              :class="['seg', { active: ecLevel === lvl, locked: densityLocked }]"
              :title="densityLocked ? 'Доступно с Pro и выше' : EC_HINTS[lvl]"
            >
              <input
                type="radio"
                v-model="ecLevel"
                :value="lvl"
                :disabled="densityLocked"
              />{{ lvl }}{{ densityLocked && lvl !== 'M' ? ' 🔒' : '' }}
            </label>
          </div>
        </UiField>

        <!-- inbox-258: logo upload. Pro+ only. -->
        <UiField
          label="Логотип в центре"
          :hint="logoLocked ? 'Загрузка лого — Pro и выше.' : 'PNG / JPEG / SVG / WebP, до 256 KB. Используйте плотность H для надёжности скана.'"
        >
          <div
            v-if="!logoLocked"
            class="logo-upload-row"
          >
            <label class="logo-input-label">
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                :disabled="submitting"
                class="logo-input"
                @change="onLogoChange"
              />
              <span class="logo-input-btn">{{ logoUrl ? 'Заменить файл…' : 'Выбрать файл…' }}</span>
            </label>
            <span
              v-if="logoName"
              class="logo-file-name"
            >{{ logoName }}</span>
            <button
              v-if="logoUrl"
              type="button"
              class="logo-remove"
              @click="removeLogo"
            >Убрать</button>
          </div>
          <UiDevCard
            v-else
            feature="Логотип — Pro/Business/Enterprise"
            message="Текущий тариф — Free. Чтобы загрузить логотип в QR, обновите подписку."
          >
            Free даёт PNG без брендинга. В Pro и выше — SVG / EPS / PDF, лого в центре, кастом плотность.
          </UiDevCard>
        </UiField>

        <p
          v-if="error"
          class="form-msg err"
          role="alert"
        >
          {{ error }}
        </p>
      </div>

      <div class="qr-preview">
        <div class="preview-frame">
          <!-- inbox-233 — client-side render через qrcode library.
               Логотип / shape / frame применяются только в download
               (backend Pillow). На preview видны цвета + EC level. -->
          <canvas ref="previewCanvas" aria-label="QR preview" />
        </div>
        <p class="preview-hint">{{ targetUrl }}</p>
        <div class="preview-actions">
          <a :href="downloadUrl" :download="`qr.${format.toLowerCase()}`" class="download-btn">Скачать {{ format }}</a>
        </div>
      </div>
    </div>

    <template #footer>
      <UiButton variant="ghost" :disabled="submitting" @click="close">Закрыть</UiButton>
      <UiButton
        :loading="submitting"
        :disabled="!canSubmit"
        @click="saveAsTemplate"
      >Сохранить как шаблон</UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.qr-split { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.qr-controls { display: flex; flex-direction: column; }
.qr-preview { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 16px; background: var(--bg-elev); border-radius: 12px; border: 1px solid var(--border); }
.preview-frame { background: #fff; padding: 16px; border-radius: 10px; display: inline-flex; }
.preview-frame canvas { width: 280px; height: 280px; display: block; image-rendering: pixelated; }
.preview-hint { font-size: 12.5px; color: var(--fg-muted); font-family: 'SF Mono', Menlo, monospace; word-break: break-all; max-width: 100%; text-align: center; }
.preview-actions { display: flex; gap: 8px; }
.download-btn { display: inline-flex; align-items: center; min-height: 40px; padding: 0 16px; background: var(--accent); color: #fff; border-radius: 8px; font-size: 13.5px; font-weight: 500; text-decoration: none; }
.download-btn:hover { background: var(--accent-hi); }
.color-picker { width: 48px; height: 32px; border: 1px solid var(--border); border-radius: 6px; padding: 2px; background: transparent; cursor: pointer; vertical-align: middle; }
.color-hex { margin-left: 10px; font-family: 'SF Mono', Menlo, monospace; font-size: 12px; color: var(--fg-muted); }
.seg-group { display: flex; gap: 4px; background: var(--bg-elev); padding: 3px; border-radius: 8px; border: 1px solid var(--border); }
.seg { flex: 1; padding: 6px 8px; text-align: center; font-size: 12.5px; color: var(--fg-muted); cursor: pointer; border-radius: 5px; }
.seg input { display: none; }
.seg.active { background: var(--accent-soft); color: var(--accent-hi); font-weight: 500; }
.select { width: 100%; padding: 0 14px; min-height: 40px; background: var(--bg-elev); border: 1px solid var(--border); border-radius: 8px; color: var(--fg); font: inherit; font-size: 13.5px; }
.form-msg { padding: 8px 12px; font-size: 12.5px; border-radius: 6px; margin-top: 8px; }
.form-msg.err { color: var(--danger); background: var(--danger-soft); }
.seg.locked { opacity: 0.5; cursor: not-allowed; }
.seg.locked input { cursor: not-allowed; }
.logo-upload-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.logo-input-label { display: inline-block; cursor: pointer; }
.logo-input { position: absolute; opacity: 0; width: 1px; height: 1px; pointer-events: none; }
.logo-input-btn { display: inline-flex; align-items: center; padding: 7px 12px; background: var(--bg-elev); border: 1px solid var(--border); border-radius: 8px; font-size: 12.5px; color: var(--fg); }
.logo-input-label:hover .logo-input-btn { border-color: var(--accent); }
.logo-file-name { font-size: 12px; color: var(--fg-muted); overflow: hidden; text-overflow: ellipsis; max-width: 200px; white-space: nowrap; }
.logo-remove { background: transparent; border: 0; color: var(--danger); font-size: 12px; cursor: pointer; font-family: inherit; padding: 4px 6px; border-radius: 4px; }
.logo-remove:hover { background: var(--danger-soft); }
@media (max-width: 760px) { .qr-split { grid-template-columns: 1fr; } }
</style>
